'use client';

import { PiggyBankIcon, ShieldIcon, CreditCardIcon, CaretRightIcon } from '@phosphor-icons/react';
import type { GoalType } from '@/types';

interface Props {
  onSelect: (type: GoalType) => void;
  reserveAlreadyExists?: boolean;
}

interface TypeCard {
  type: GoalType;
  Icon: React.ElementType;
  title: string;
  subtitle: string;
  examples: string[];
  accent: string;
  iconBg: string;
  iconColor: string;
}

const CARDS: TypeCard[] = [
  {
    type: 'Savings',
    Icon: PiggyBankIcon,
    title: 'Poupança',
    subtitle: 'Objetivo com prazo definido.',
    examples: ['Viagem', 'Entrada de imóvel', 'Trocar de carro'],
    accent: 'hover:border-success-border hover:shadow-success-border',
    iconBg: 'bg-success-surface',
    iconColor: 'text-success',
  },
  {
    type: 'Reserve',
    Icon: ShieldIcon,
    title: 'Reserva',
    subtitle: 'Colchão de emergência em meses de despesas.',
    examples: ['Reserva 6 meses', 'Imprevistos médicos', 'Perda de renda'],
    accent: 'hover:border-info-border hover:shadow-info-border',
    iconBg: 'bg-info-surface',
    iconColor: 'text-info',
  },
  {
    type: 'DebtPayoff',
    Icon: CreditCardIcon,
    title: 'Dívida',
    subtitle: 'Quitar um saldo devedor.',
    examples: ['Cartão de crédito', 'Empréstimo pessoal', 'Financiamento'],
    accent: 'hover:border-error-border hover:shadow-error-border',
    iconBg: 'bg-error-surface',
    iconColor: 'text-error',
  },
];

export function TypePicker({ onSelect, reserveAlreadyExists = false }: Props) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-text-primary sm:text-xl">Que tipo de meta?</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Escolha o formato para que possamos guiar os próximos passos.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        {CARDS.map((c) => {
          const disabled = c.type === 'Reserve' && reserveAlreadyExists;
          return (
          <button
            key={c.type}
            type="button"
            onClick={() => { if (!disabled) onSelect(c.type); }}
            disabled={disabled}
            className={`group text-left p-4 bg-surface border border-border-subtle rounded-2xl transition-colors motion-reduce:transition-none ${disabled ? 'opacity-60 cursor-not-allowed' : `${c.accent}`}`}
          >
            <div className={`size-11 rounded-xl flex items-center justify-center mb-4 ${c.iconBg} ${c.iconColor}`}>
              <c.Icon className="size-6" weight="duotone" />
            </div>
            <h3 className="mb-1 text-base font-bold text-text-primary">{c.title}</h3>
            <p className="mb-3 text-xs/relaxed text-text-secondary">{c.subtitle}</p>
            <ul className="mb-4 space-y-1">
              {c.examples.map((e) => (
                <li key={e} className="flex items-center gap-1.5 text-2xs text-text-tertiary">
                  <span className="size-1 rounded-full bg-muted" />
                  {e}
                </li>
              ))}
            </ul>
            {disabled ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-text-tertiary">
                Já criada
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-text-primary group-hover:opacity-80">
                Começar <CaretRightIcon size={12} weight="bold" />
              </span>
            )}
          </button>
          );
        })}
      </div>
    </div>
  );
}
